/* Kayleigh Starr — Editions-style homepage background.
 *
 * A fixed full-screen WebGL2 canvas behind the homepage, running the same
 * post-processing recipe as Shopify's Editions Winter '26 page:
 *   • the hero image is revealed with the radial, mouse-following "hero"
 *     crossfade (edge-detect sketch → image, with a zoom)
 *   • a solid sheet (grey-light) with the "Overlay" torn edge (ramp + Perlin +
 *     mud threshold, hard edge, HDR glow → bloom) sits just above the first
 *     content section, pinned to the hero's bottom edge, and is part of the
 *     scene the intro reveals
 *   • optionally a second torn edge above the footer shows the image again
 *   • an optional second canvas above the page reveals the DOM with the same intro
 *
 * window.KLSEditionsHome.mount({ canvas, image, sheetColor, sheetColorDark,
 *   heroSelector, tearInset, lean, tearOutSelector, tearOutInset, maskCanvas, bloom })
 *
 * sheetColorDark is used while html[data-theme="dark"] is set (the header's
 * toggle); the attribute is re-read every frame so the paper switches live.
 */
(function () {
  'use strict';

  /* ---------------- tiling Perlin noise (same algorithm as the site) ---------------- */
  function mulberry32(a) {
    return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  }
  function makePerlin(seed) {
    var perm = new Uint8Array(512), i, j, t, rnd = mulberry32(seed);
    for (i = 0; i < 256; i++) perm[i] = i;
    for (i = 255; i > 0; i--) { j = Math.floor(rnd() * (i + 1)); t = perm[i]; perm[i] = perm[j]; perm[j] = t; }
    for (i = 0; i < 256; i++) perm[256 + i] = perm[i];
    var grads = [[1, 1], [-1, 1], [1, -1], [-1, -1], [1, 0], [-1, 0], [0, 1], [0, -1]];
    function fade(x) { return x * x * x * (x * (x * 6 - 15) + 10); }
    function lerp(a, b, x) { return a + x * (b - a); }
    function grad(h, x, y) { var g = grads[h & 7]; return g[0] * x + g[1] * y; }
    function noise(x, y, period) {
      var X = Math.floor(x) & 255, Y = Math.floor(y) & 255, xf = x - Math.floor(x), yf = y - Math.floor(y);
      var x1 = (X + 1) % period, y1 = (Y + 1) % period, x0 = X % period, y0 = Y % period, u = fade(xf), v = fade(yf);
      var aa = perm[perm[x0] + y0], ab = perm[perm[x0] + y1], ba = perm[perm[x1] + y0], bb = perm[perm[x1] + y1];
      return lerp(lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u), lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u), v);
    }
    function fbm(x, y, period, oct) {
      var sum = 0, amp = 0.5, freq = 1, p = period, o;
      for (o = 0; o < (oct || 4); o++) { sum += amp * noise(x * freq, y * freq, p); amp *= 0.5; freq *= 2; p *= 2; }
      return sum;
    }
    return { noise: noise, fbm: fbm };
  }
  function dataTexture(gl, data, w, h) {
    var t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  }
  function noiseTexture(gl) {
    var S = 256, P = 8, p = makePerlin(101), d = new Uint8Array(S * S * 4), x, y, v, b, i;
    for (y = 0; y < S; y++) for (x = 0; x < S; x++) {
      v = (p.fbm(x / S * P, y / S * P, P) + 1) * 0.5; b = Math.max(0, Math.min(255, Math.floor(v * 255)));
      i = (y * S + x) * 4; d[i] = d[i + 1] = d[i + 2] = b; d[i + 3] = 255;
    }
    return dataTexture(gl, d, S, S);
  }
  /* procedural stand-in for the site's mud/plaster normal map (red channel = x-slope) */
  function mudTexture(gl) {
    var S = 256, p = makePerlin(7), rnd = mulberry32(99), h = new Float32Array(S * S), x, y, u, v, val, k, s, streaks = [];
    for (k = 0; k < 40; k++) streaks.push({ x: rnd(), y: rnd(), a: rnd() * Math.PI, l: 0.03 + rnd() * 0.12, w: 0.003 + rnd() * 0.005, s: (rnd() - 0.5) * 0.5 });
    for (y = 0; y < S; y++) for (x = 0; x < S; x++) {
      u = x / S; v = y / S;
      val = p.fbm(u * 4, v * 4, 4, 3) + 0.35 * p.noise(u * 32, v * 32, 32) + 0.25 * p.noise(u * 64, v * 64, 64) + 0.18 * p.noise(u * 128, v * 128, 128);
      for (k = 0; k < streaks.length; k++) {
        s = streaks[k]; var dx = u - s.x, dy = v - s.y; dx -= Math.round(dx); dy -= Math.round(dy);
        var ca = Math.cos(s.a), sa = Math.sin(s.a), along = dx * ca + dy * sa, across = -dx * sa + dy * ca;
        if (Math.abs(along) < s.l && Math.abs(across) < s.w) val += s.s * (1 - Math.abs(across) / s.w);
      }
      h[y * S + x] = val;
    }
    var gx = new Float32Array(S * S), mean = 0, sd = 0, i;
    for (y = 0; y < S; y++) for (x = 0; x < S; x++) gx[y * S + x] = (h[y * S + ((x + 1) % S)] - h[y * S + ((x + S - 1) % S)]) * 0.5;
    for (i = 0; i < gx.length; i++) mean += gx[i]; mean /= gx.length;
    for (i = 0; i < gx.length; i++) sd += (gx[i] - mean) * (gx[i] - mean); sd = Math.sqrt(sd / gx.length) || 1;
    var scale = 0.067 / sd, d = new Uint8Array(S * S * 4);
    for (i = 0; i < S * S; i++) { d[i * 4] = Math.max(0, Math.min(255, (0.495 + (gx[i] - mean) * scale) * 255)); d[i * 4 + 1] = 128; d[i * 4 + 2] = 245; d[i * 4 + 3] = 255; }
    return dataTexture(gl, d, S, S);
  }

  /* ---------------- shaders (verbatim ports of the CrossFade + Overlay effects) ---------------- */
  var VERT = '#version 300 es\nin vec2 aPos; out vec2 vUv; void main(){ vUv = aPos*0.5+0.5; gl_Position = vec4(aPos,0.0,1.0); }';
  var COMPOSITE = [
    '#version 300 es', 'precision highp float;', 'in vec2 vUv; out vec4 fragColor;',
    'uniform sampler2D tImage, tMudNormal, tNoise;',
    'uniform float uImgAspect, uAspect, uTime, uProgress, uIsHero, uDarken, uPosition, uZoom, uFade, uLean, uEdgeLight;',
    'uniform vec2 uResolution, uMouse, uPan;',
    'uniform vec3 uColor;',
    'vec4 sampleScene(vec2 uv){',
    '  vec2 ratio = vec2(min(uAspect / uImgAspect, 1.0), min(uImgAspect / uAspect, 1.0));',
    '  vec2 c = vec2(uv.x * ratio.x + (1.0 - ratio.x) * 0.5, uv.y * ratio.y + (1.0 - ratio.y) * 0.5);',
    '  c = (c - 0.5) / uZoom + 0.5 + uPan; c.y = 1.0 - c.y;',
    '  return vec4(pow(texture(tImage, c).rgb, vec3(2.2)) * uFade, 1.0);',
    '}',
    'float sampleNoise(vec2 uv){ return texture(tNoise, uv).r * 2.0 - 1.0; }',
    'vec4 overlay(vec4 inputColor, vec2 uv);',
    'vec4 crossFade(vec2 uv){',
    '  bool isHero = uIsHero > 0.5;',
    '  // radius grows linearly with progress, so uProgress drives it directly (the',
    '  // original smoothstep(0.0, 1.5, ..) tops out at 0.74 and left the finish to',
    '  // the threshold collapse below, which read as a speed-up at the end)',
    '  float progress = uProgress;',
    '  vec2 sceneCenter = vec2(0.5, 0.65);',
    '  vec2 currentUV = uv, nextUV = uv;',
    '  if (isHero) {',
    '    currentUV = (uv - sceneCenter) * (1.0 - smoothstep(0.2, 1.0, uProgress) * 0.1) + sceneCenter;',
    '    nextUV = (uv - sceneCenter) * (1.0 + smoothstep(0.8, 0.0, uProgress) * 0.1) + sceneCenter;',
    '  }',
    '  vec4 current = vec4(0.0, 0.0, 0.0, 1.0);',          // scene A: black (the page before load)
    '  vec4 next = overlay(sampleScene(nextUV), nextUV);',   // the sheet is part of the scene, so the reveal uncovers it too
    '  if (uProgress >= 0.999) { current = next; }',
    '  vec3 currentEdges = vec3(0.0), nextEdges = vec3(0.0);',
    '  if (uProgress > 0.01 && uProgress < 0.99 || uDarken > 0.001) {',
    '    float currentLuma = dot(current.rgb, vec3(0.299, 0.587, 0.114));',
    '    currentEdges = vec3(fwidth(currentLuma) * mix(5.0, 10.0, progress));',
    '    float nextLuma = dot(next.rgb, vec3(0.299, 0.587, 0.114));',
    '    nextEdges = vec3(fwidth(nextLuma) * mix(5.0, 10.0, 1.0 - progress));',
    '  }',
    '  vec3 mudNormal = texture(tMudNormal, uv * 2.0).rgb;',
    '  float mudStrength = isHero ? mix(0.2, 0.4, 0.5 + 0.5 * sin(uTime - uv.x * 10.0)) : mix(0.3, 0.6, 0.5 + 0.5 * sin(uTime - uv.x * 10.0));',
    '  float mudOffset = (mudNormal.r - 0.5) * mudStrength;',
    '  vec2 aspectUv = vec2(uv.x * uAspect, uv.y) + vec2(0.0, uTime * 0.02);',
    '  float noiseSpeed = isHero ? 0.07 : 0.05;',
    '  float currentNoise = sampleNoise(aspectUv * 0.25 - uTime * noiseSpeed * 0.125);',
    '  vec2 maskCenter = isHero ? mix(sceneCenter, uMouse, 0.1) : vec2(0.5);',
    '  float threshold;',
    '  if (isHero) {',
    '    float dist = length((uv - maskCenter) * vec2(uAspect, 1.0)) * 0.8;',
    '    threshold = mix(dist, uv.x, smoothstep(0.6, -0.4, abs(uv.x - sceneCenter.x)) * mix(0.0, 0.4, smoothstep(0.05, 0.5, progress)));',
    '    threshold = mix(threshold, 0.0, smoothstep(0.8, 1.0, uProgress));',
    '  } else {',
    '    float ease = mix(progress * progress * (3.0 - 2.0 * progress), progress, 0.25);',
    '    threshold = mix(uv.y, uv.x, smoothstep(0.6, -0.4, abs(uv.x - 0.5)) * 0.5);',
    '    progress = ease;',
    '  }',
    '  threshold = threshold * 2.0 - 1.0;',
    '  threshold = threshold / 1.2 + currentNoise * 0.2 + mudOffset;',
    '  threshold = threshold * 0.5 + 0.5;',
    '  float edge = progress - threshold;',
    '  float aa = fwidth(edge) * 10.0;',
    '  float blendFactor = smoothstep(-aa, aa, edge);',
    '  current = mix(current, vec4(currentEdges, 1.0), smoothstep(0.0, 0.5, progress));',
    '  next = mix(next, vec4(nextEdges, 1.0), smoothstep(0.2, 0.8, 1.0 - progress));',
    '  if (uDarken > 0.001) current = mix(current, vec4(0.02 * uDarken + currentEdges * 0.1, 1.0), uDarken);',
    '  vec4 outputColor = mix(current, next, blendFactor);',
    '  float glowStrength = isHero ? mix(40.0, 8.0, smoothstep(0.05, 0.25, progress)) : mix(2.0, 10.0, 0.5);',
    '  float glowThreshold = isHero ? glowStrength * 0.001 : 0.003;',
    '  float glowFactor = smoothstep(0.0, glowThreshold, abs(edge));',
    '  float glowMult = isHero ? mix(glowStrength * 0.5, glowStrength, 0.5 + 0.5 * currentNoise * sin(uTime + uv.x * 10.0)) : mix(2.0, 10.0, 0.5 + 0.5 * currentNoise * sin(uTime + uv.x * 10.0));',
    '  if (uProgress > 0.001 && uProgress < 0.999) outputColor = mix(outputColor, outputColor * glowMult, 1.0 - glowFactor);',
    '  return outputColor;',
    '}',
    'vec4 overlay(vec4 inputColor, vec2 uv){',
    '  if (abs(uPosition) >= 1.0) return inputColor;',
    '  vec3 mudNormal = texture(tMudNormal, uv * 2.0).rgb;',
    '  float mudOffset = (mudNormal.r - 0.5) * mix(0.3, 0.6, 0.5 + 0.5 * sin(uTime - uv.x * 10.0)) * 0.1;',
    '  float aspect = uResolution.x / uResolution.y;',
    '  vec2 aspectUv = vec2(uv.x * aspect, uv.y) + vec2(0.0, uTime * 0.02);',
    '  float noiseValue = sampleNoise(aspectUv * 0.375 - uTime * 0.00625);',
    '  float threshold = mix(uv.y, uv.x, smoothstep(0.6, -0.4, abs(uv.x - 0.4)) * uLean);',
    '  threshold = threshold * 2.0 - 1.0;',
    '  threshold = threshold / 1.2 + noiseValue * 0.2 + mudOffset;',
    '  threshold = threshold * 0.5 + 0.5;',
    '  float progress; float thresholdFinal = threshold;',
    '  if (uPosition <= 0.0) { progress = uPosition + 1.0; } else { progress = 1.0 - uPosition; thresholdFinal = 1.0 - threshold; }',
    '  float edge = progress - thresholdFinal;',
    '  float aa = fwidth(edge) * 10.0;',
    '  float alpha = smoothstep(-aa, aa, edge);',
    '  float glowFactor = smoothstep(0.0, 0.005, abs(edge));',
    '  vec3 color = pow(uColor, vec3(2.2));',
    '  // the fringe is the sheet colour pushed past white (HDR -> bloom); a dark sheet',
    '  // times anything stays dark, so uEdgeLight adds a lit edge in that case',
    '  float fringe = mix(2.0, 4.0, 0.5 + 0.5 * sin(uTime + uv.x * 10.0));',
    '  vec3 lit = color * fringe + vec3(uEdgeLight) * fringe * 0.5;',
    '  color = mix(color, lit, 1.0 - glowFactor);',
    '  return vec4(mix(inputColor.rgb, color, alpha), 1.0);',
    '}',
    'void main(){',
    '  vec4 c = crossFade(vUv);',
    '  float luma = dot(c.rgb, vec3(0.2126, 0.7152, 0.0722));',
    '  fragColor = vec4(min(c.rgb, vec3(1.0)), clamp((luma - 1.0) / 4.0, 0.0, 1.0));',
    '}'
  ].join('\n');
  // Same reveal maths as crossFade(), but for a canvas that sits ABOVE the page: paints
  // black wherever the scene is not revealed yet, so the DOM content is uncovered by the
  // same radial tear as the image.  The content inside the revealed area fades in on the
  // same curve the image uses to go from edge-sketch to full colour.
  var MASK = [
    '#version 300 es', 'precision highp float;', 'in vec2 vUv; out vec4 fragColor;',
    'uniform sampler2D tMudNormal, tNoise;',
    'uniform float uAspect, uTime, uProgress;',
    'uniform vec2 uMouse;',
    'float sampleNoise(vec2 uv){ return texture(tNoise, uv).r * 2.0 - 1.0; }',
    'void main(){',
    '  vec2 uv = vUv;',
    '  float progress = uProgress;',
    '  vec2 sceneCenter = vec2(0.5, 0.65);',
    '  vec3 mudNormal = texture(tMudNormal, uv * 2.0).rgb;',
    '  float mudStrength = mix(0.2, 0.4, 0.5 + 0.5 * sin(uTime - uv.x * 10.0));',
    '  float mudOffset = (mudNormal.r - 0.5) * mudStrength;',
    '  vec2 aspectUv = vec2(uv.x * uAspect, uv.y) + vec2(0.0, uTime * 0.02);',
    '  float currentNoise = sampleNoise(aspectUv * 0.25 - uTime * 0.07 * 0.125);',
    '  vec2 maskCenter = mix(sceneCenter, uMouse, 0.1);',
    '  float dist = length((uv - maskCenter) * vec2(uAspect, 1.0)) * 0.8;',
    '  float threshold = mix(dist, uv.x, smoothstep(0.6, -0.4, abs(uv.x - sceneCenter.x)) * mix(0.0, 0.4, smoothstep(0.05, 0.5, progress)));',
    '  threshold = mix(threshold, 0.0, smoothstep(0.8, 1.0, uProgress));',
    '  threshold = threshold * 2.0 - 1.0;',
    '  threshold = threshold / 1.2 + currentNoise * 0.2 + mudOffset;',
    '  threshold = threshold * 0.5 + 0.5;',
    '  float edge = progress - threshold;',
    '  float aa = fwidth(edge) * 10.0;',
    '  float blendFactor = smoothstep(-aa, aa, edge);',
    '  float sketch = smoothstep(0.2, 0.8, 1.0 - progress);',      // image is still a sketch: content not in yet
    '  float cover = 1.0 - blendFactor * (1.0 - sketch);',
    '  fragColor = vec4(0.0, 0.0, 0.0, cover);',
    '}'
  ].join('\n');
  var PREFILTER = '#version 300 es\nprecision highp float; in vec2 vUv; out vec4 fragColor; uniform sampler2D tSrc; uniform vec2 uTexel;\nvoid main(){ vec3 s=vec3(0.0); for(int y=-1;y<=1;y++) for(int x=-1;x<=1;x++){ vec4 c=texture(tSrc, vUv+vec2(float(x),float(y))*uTexel); s+=c.rgb*c.a*4.0; } fragColor=vec4(s/9.0,1.0); }';
  var BLUR = '#version 300 es\nprecision highp float; in vec2 vUv; out vec4 fragColor; uniform sampler2D tSrc; uniform vec2 uDir;\nvoid main(){ float w[5]=float[](0.227027,0.1945946,0.1216216,0.054054,0.016216); vec3 s=texture(tSrc,vUv).rgb*w[0]; for(int i=1;i<5;i++){ s+=texture(tSrc,vUv+uDir*float(i)).rgb*w[i]; s+=texture(tSrc,vUv-uDir*float(i)).rgb*w[i]; } fragColor=vec4(s,1.0); }';
  var FINAL = '#version 300 es\nprecision highp float; in vec2 vUv; out vec4 fragColor; uniform sampler2D tScene,tBloomA,tBloomB; uniform float uBloom;\nvoid main(){ vec4 s=texture(tScene,vUv); vec3 hdr=s.rgb*(1.0+s.a*4.0*step(0.999,max(s.r,max(s.g,s.b)))); vec3 bloom=texture(tBloomA,vUv).rgb*0.6+texture(tBloomB,vUv).rgb*0.4; vec3 c=hdr+bloom*uBloom; c=c/(1.0+max(vec3(0.0),c-1.0)*0.5); fragColor=vec4(pow(clamp(c,0.0,1.0),vec3(1.0/2.2)),1.0); }';

  function compile(gl, type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.error('[kls-editions]', gl.getShaderInfoLog(s)); return null; }
    return s;
  }
  function program(gl, fs) {
    var p = gl.createProgram(), v = compile(gl, gl.VERTEX_SHADER, VERT), f = compile(gl, gl.FRAGMENT_SHADER, fs);
    if (!v || !f) return null;
    gl.attachShader(p, v); gl.attachShader(p, f); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) { console.error('[kls-editions]', gl.getProgramInfoLog(p)); return null; }
    var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS), i, info;
    for (i = 0; i < n; i++) { info = gl.getActiveUniform(p, i); u[info.name] = gl.getUniformLocation(p, info.name); }
    return { p: p, u: u };
  }
  function fbo(gl, w, h) {
    var t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    var f = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, f);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0); gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { f: f, t: t, w: w, h: h };
  }
  function hexToRgb(hex) {
    hex = String(hex || '#dcdcd0').replace('#', ''); if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(hex, 16); return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }


  /* ---------------- mount ---------------- */
  function mount(opts) {
    var canvas = opts.canvas, html = document.documentElement;
    var gl = canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'high-performance' });
    if (!gl) { html.setAttribute('data-kls-editions', 'fallback'); return; }
    var composite = program(gl, COMPOSITE), prefilter = program(gl, PREFILTER), blur = program(gl, BLUR), final = program(gl, FINAL);
    if (!composite || !prefilter || !blur || !final) { html.setAttribute('data-kls-editions', 'fallback'); return; }
    html.setAttribute('data-kls-editions', 'on');

    // optional second canvas above the page: reveals the DOM with the same radial tear
    var mask = null;
    if (opts.maskCanvas && !(window.scrollY > 100)) {
      var mgl = opts.maskCanvas.getContext('webgl2', { antialias: false, alpha: true, premultipliedAlpha: false });
      var mprog = mgl && program(mgl, MASK);
      if (mgl && mprog) {
        var mquad = mgl.createBuffer(); mgl.bindBuffer(mgl.ARRAY_BUFFER, mquad);
        mgl.bufferData(mgl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), mgl.STATIC_DRAW);
        var mvao = mgl.createVertexArray(); mgl.bindVertexArray(mvao); mgl.enableVertexAttribArray(0); mgl.vertexAttribPointer(0, 2, mgl.FLOAT, false, 0, 0);
        mask = { gl: mgl, p: mprog, canvas: opts.maskCanvas, vao: mvao, tNoise: noiseTexture(mgl), tMud: mudTexture(mgl) };
        mgl.enable(mgl.BLEND); mgl.blendFunc(mgl.SRC_ALPHA, mgl.ONE_MINUS_SRC_ALPHA);
      }
    }
    html.setAttribute('data-kls-editions-intro', mask ? 'running' : 'done');

    var quad = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var vao = gl.createVertexArray(); gl.bindVertexArray(vao); gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    var tNoise = noiseTexture(gl), tMud = mudTexture(gl);
    var black = dataTexture(gl, new Uint8Array([0, 0, 0, 255]), 1, 1);
    var tImage = null, imgAspect = 1.5, loaded = false;
    var img = new Image(); img.crossOrigin = 'anonymous'; img.decoding = 'async';
    img.onload = function () {
      tImage = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tImage);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.generateMipmap(gl.TEXTURE_2D);
      imgAspect = img.naturalWidth / img.naturalHeight; loaded = true; introStart = null;
      html.setAttribute('data-kls-editions-loaded', 'true');
    };
    img.onerror = function () { loaded = true; introStart = null; html.setAttribute('data-kls-editions-loaded', 'true'); };
    setTimeout(function () { if (!loaded) img.onerror(); }, 8000);   // never leave the page black
    img.src = opts.image;

    var sheet = hexToRgb(opts.sheetColor), sheetDark = opts.sheetColorDark ? hexToRgb(opts.sheetColorDark) : sheet;
    // lit-edge strength from the sheet's linear luminance: 0 for paper-light
    // colours, up to 1 for near-black ones
    function edgeLight(c) {
      var l = 0.2126 * Math.pow(c[0], 2.2) + 0.7152 * Math.pow(c[1], 2.2) + 0.0722 * Math.pow(c[2], 2.2);
      return Math.max(0, Math.min(1, 1 - l * 4));
    }
    var W = 0, H = 0, dpr = 1, sceneFbo, bloomA, bloomB, bloomC, bloomD;
    function resize() {
      var w = window.innerWidth, h = window.innerHeight;
      dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2, 1920 / w, 1920 / h));
      W = Math.round(w * dpr); H = Math.round(h * dpr);
      canvas.width = W; canvas.height = H; canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      sceneFbo = fbo(gl, W, H);
      bloomA = fbo(gl, Math.max(1, W >> 2), Math.max(1, H >> 2)); bloomB = fbo(gl, bloomA.w, bloomA.h);
      bloomC = fbo(gl, Math.max(1, W >> 3), Math.max(1, H >> 3)); bloomD = fbo(gl, bloomC.w, bloomC.h);
      if (mask) { mask.canvas.width = W; mask.canvas.height = H; mask.canvas.style.width = w + 'px'; mask.canvas.style.height = h + 'px'; }
    }
    resize(); window.addEventListener('resize', resize);
    // paint the mask opaque now (after resize(), which resets the canvas to transparent),
    // still inside this synchronous mount, so the page never shows through between the
    // CSS cover (the hero's .kls-ed-mask rule) and the first reveal frame
    if (mask) { mask.gl.clearColor(0, 0, 0, 1); mask.gl.clear(mask.gl.COLOR_BUFFER_BIT); }

    var mouse = [0.5, 0.5], smooth = [0.5, 0.5];
    window.addEventListener('pointermove', function (e) { mouse[0] = e.clientX / window.innerWidth; mouse[1] = e.clientY / window.innerHeight; }, { passive: true });
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var introStart = null, intro = window.scrollY > 100 ? 1 : 0;   // radial reveal, 0→1
    var heroEl = document.querySelector(opts.heroSelector), outEl = opts.tearOutSelector ? document.querySelector(opts.tearOutSelector) : null;
    var themeNow = '';

    function frame(now) {
      var t = now / 1000, vh = window.innerHeight, scroll = window.scrollY || 0;
      // ---- intro: the hero-style radial reveal (4 s, starts once the image is in).
      //      Linear: the shader's reveal radius is linear in progress, so this is
      //      what keeps the circle growing at one steady rate
      if (intro < 1 && loaded) {
        if (introStart === null) introStart = now + 100;
        intro = reduced ? 1 : Math.max(0, Math.min(1, (now - introStart) / 4000));
      }
      // ---- the sheet's torn edge is pinned just above the hero's bottom edge (so it sits
      //      right above the first content section and scrolls with the page). The overlay
      //      covers where uv.y < position + 1, so position = -(edge height from the bottom).
      var hr = heroEl ? heroEl.getBoundingClientRect() : { top: 0, bottom: vh };
      var heroBottomVh = hr.bottom / vh;                       // 1 = viewport bottom, 0 = top
      var inset = opts.tearInset == null ? 0.1 : opts.tearInset;
      var heroProg = Math.max(0, Math.min(1, -hr.top / Math.max(1, hr.height)));   // 0..1 as the hero scrolls away
      var pos = Math.max(-1, Math.min(0, -(heroBottomVh - inset)));
      // optional tear-out: a second torn edge pinned a set height above the footer; the
      // image shows again between that edge and the footer (position > 0 = wipe-out branch,
      // covered where uv.y > position)
      var outProg = 0;
      if (outEl) {
        var oTop = outEl.getBoundingClientRect().top;
        var outInset = opts.tearOutInset == null ? 0.1 : opts.tearOutInset;
        var edgeVh = 1 - (oTop / vh) + outInset;             // edge height from the viewport bottom
        if (edgeVh > 0) { outProg = Math.min(1, edgeVh); pos = Math.min(1, edgeVh); }
      }
      pos = Math.max(-1, Math.min(1, pos));
      // ---- header theme: light ink over the dark image, dark ink over the sheet (site thresholds)
      var theme = (pos >= -0.25 && pos <= 0.75) ? 'light' : 'dark';
      if (theme !== themeNow) { themeNow = theme; html.setAttribute('data-kls-bg', theme); }

      smooth[0] += (mouse[0] - smooth[0]) * 0.15; smooth[1] += (mouse[1] - smooth[1]) * 0.15;

      gl.bindVertexArray(vao); gl.disable(gl.DEPTH_TEST); gl.disable(gl.BLEND);
      // pass 1
      gl.bindFramebuffer(gl.FRAMEBUFFER, sceneFbo.f); gl.viewport(0, 0, W, H);
      var P = composite; gl.useProgram(P.p);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tImage || black); gl.uniform1i(P.u.tImage, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, tMud); gl.uniform1i(P.u.tMudNormal, 1);
      gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, tNoise); gl.uniform1i(P.u.tNoise, 2);
      gl.uniform1f(P.u.uImgAspect, imgAspect); gl.uniform1f(P.u.uAspect, W / H); gl.uniform1f(P.u.uTime, t);
      gl.uniform1f(P.u.uProgress, loaded ? intro : 0); gl.uniform1f(P.u.uIsHero, 1);
      gl.uniform1f(P.u.uDarken, 0); gl.uniform1f(P.u.uPosition, pos);
      gl.uniform1f(P.u.uZoom, 1.08 + heroProg * 0.06 + outProg * 0.08);   // keeps zooming at the bottom too
      gl.uniform1f(P.u.uFade, 1);
      gl.uniform1f(P.u.uLean, opts.lean == null ? 0.5 : opts.lean);
      gl.uniform2f(P.u.uResolution, W, H); gl.uniform2f(P.u.uMouse, 1 - smooth[0], 1 - smooth[1]);
      gl.uniform2f(P.u.uPan, (smooth[0] - 0.5) * 0.02, (smooth[1] - 0.5) * 0.015 - heroProg * 0.05 - outProg * 0.05);
      var col = html.getAttribute('data-theme') === 'dark' ? sheetDark : sheet;
      gl.uniform3f(P.u.uColor, col[0], col[1], col[2]);
      gl.uniform1f(P.u.uEdgeLight, edgeLight(col));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      // bloom chain
      function chain(src, a, b) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, a.f); gl.viewport(0, 0, a.w, a.h); gl.useProgram(prefilter.p);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, src.t); gl.uniform1i(prefilter.u.tSrc, 0);
        gl.uniform2f(prefilter.u.uTexel, 1 / src.w, 1 / src.h); gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.useProgram(blur.p);
        for (var i = 0; i < 2; i++) {
          gl.bindFramebuffer(gl.FRAMEBUFFER, b.f); gl.bindTexture(gl.TEXTURE_2D, a.t); gl.uniform1i(blur.u.tSrc, 0); gl.uniform2f(blur.u.uDir, 1 / a.w, 0); gl.drawArrays(gl.TRIANGLES, 0, 3);
          gl.bindFramebuffer(gl.FRAMEBUFFER, a.f); gl.bindTexture(gl.TEXTURE_2D, b.t); gl.uniform2f(blur.u.uDir, 0, 1 / a.h); gl.drawArrays(gl.TRIANGLES, 0, 3);
        }
      }
      chain(sceneFbo, bloomA, bloomB); chain(bloomA, bloomC, bloomD);
      // final
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, W, H); gl.useProgram(final.p);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, sceneFbo.t); gl.uniform1i(final.u.tScene, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, bloomA.t); gl.uniform1i(final.u.tBloomA, 1);
      gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, bloomC.t); gl.uniform1i(final.u.tBloomB, 2);
      gl.uniform1f(final.u.uBloom, opts.bloom == null ? 0.5 : opts.bloom);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      // ---- reveal mask over the page (same maths, same textures, same uniforms)
      if (mask) {
        var m = mask.gl, mu = mask.p.u;
        m.bindVertexArray(mask.vao); m.viewport(0, 0, W, H); m.useProgram(mask.p.p);
        m.clearColor(0, 0, 0, 0); m.clear(m.COLOR_BUFFER_BIT);
        m.activeTexture(m.TEXTURE0); m.bindTexture(m.TEXTURE_2D, mask.tMud); m.uniform1i(mu.tMudNormal, 0);
        m.activeTexture(m.TEXTURE1); m.bindTexture(m.TEXTURE_2D, mask.tNoise); m.uniform1i(mu.tNoise, 1);
        m.uniform1f(mu.uAspect, W / H); m.uniform1f(mu.uTime, t); m.uniform1f(mu.uProgress, loaded ? intro : 0);
        m.uniform2f(mu.uMouse, 1 - smooth[0], 1 - smooth[1]);
        m.drawArrays(m.TRIANGLES, 0, 3);
        if (intro >= 1) { mask = null; html.setAttribute('data-kls-editions-intro', 'done'); }
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  window.KLSEditionsHome = { mount: mount };
})();
